import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import { FilterTab } from "../../../../components/filter-tabs";
import { useDebounce } from "../../../../hooks/usedebounce";
import { Account } from "../../../../models/account";
import {
  ChartListResponse,
  ChartStatus,
  ChartStatusEnum,
  ChartTypeEnum,
} from "../../../../models/chart";
import { AccountService } from "../../../../services/account-service";
import { ChartsService } from "../../../../services/charts-service";
import { CognitoService } from "../../../../services/cognito-service";

export type AdminManageClerkState = {
  clerk: Account;
  tabs: FilterTab[];
  selectedTab: number;
  selectedColor: string;
  setSelectedTab: (tab: number) => void;
  readyToPrintCharts: ChartListResponse[];
  otherChartTab: ChartListResponse[][];
  printedCharts: ChartListResponse[];
  counties: string[];
  otherChartStatus: string;
  downloadAll: (chartIds: string[], type: "charts" | "decks") => Promise<void>;
  downloadChart: (chartId: string, type: "download" | "print") => Promise<void>;
  updateStatus: (status: ChartStatus) => Promise<void>;
  setOtherChartQuery: (query: string) => void;
  getDate: (date: string) => string;
  debouncedQuery: string;
  otherChartQuery: string;
  printAll: (chartIds: string[]) => Promise<void>;
  downloadReadyForPrintCharts: (chartIds: string[]) => Promise<void>;
  duplicateChart: (chartId: string, chartType: ChartTypeEnum) => void;
  markChartsAsPrinted: (chartIds: string[]) => Promise<void>;
};

export default function useAdminManageClerk(): AdminManageClerkState {
  const [clerk, setClerk] = useState<Account>({} as Account);
  const [searchParams, setSearchParams] = useSearchParams();
  const { clerkId } = useParams<{ clerkId: string }>();
  const [readyToPrintCharts, setReadyToPrintCharts] = useState<
    ChartListResponse[]
  >([]);
  const [printedCharts, setPrintedCharts] = useState<ChartListResponse[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [otherChartTab, setOtherChartTab] = useState<ChartListResponse[][]>([]);
  const [otherChartQuery, setOtherChartQuery] = useState<string>("");
  const [counties, setCounties] = useState<string[]>([]);
  const debouncedQuery = useDebounce(otherChartQuery, 800);
  const [loading, setLoading] = useState<boolean>(true);
  const [otherChartStatus, setOtherChartStatus] = useState<string>("Other");
  const [shouldWebSocketConnect, setShouldWebSocketConnect] = useState(false);
  const [websocketUrl, setWebsocketUrl] = useState("");
  const [token, setToken] = useState<string>("");
  const navigate = useNavigate();

  useWebSocket(
    websocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: async (event: WebSocketEventMap["close"]) => {
        await getOtherChartTab(otherChartQuery, otherChartStatus);
        return;
      },
      onMessage: (event: WebSocketEventMap["message"]) => {
        toast.remove();
        if (event.data.includes("FINISHED")) {
          setShouldWebSocketConnect(false);
          toast.success("Chart has been regenerated");
        } else if (event.data.includes("ERROR")) {
          setShouldWebSocketConnect(false);
          toast.error("Error occurred while regenerating the chart.");
        }
      },
      onError: (event: WebSocketEventMap["error"]) => {
        setShouldWebSocketConnect(false);
        toast.error("Error occurred while generating the chart files.");
      },
      protocols: ["Authorization", token],
    },
    shouldWebSocketConnect
  );

  const markChartsAsPrinted = async (chartIds: string[]) => {
    await toast.promise(ChartsService.markPrintRequestedChartsAsPrinted(chartIds), {
      loading: "Loading...",
      error: "Failed to mark charts as printed!",
      success: "Successfully marked charts as printed!",
    });
    getReadyToPrintCharts();
    getPrintedCharts();
  }

  const regenerateChart = async (id: string) => {
    const url = `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/charts/admin/process/${id}`;

    const session = await CognitoService.getSession();
    if (session && !token) {
      setToken(session.getIdToken().getJwtToken());
    }
    setWebsocketUrl(url);
    setShouldWebSocketConnect(true);
  };

  const deleteChart = async (id: string) => {
    await ChartsService.deleteChart(id);
    await getOtherChartTab(otherChartQuery, otherChartStatus);
  };

  useEffect(() => {
    if (selectedTab !== 2) {
      setOtherChartQuery("");
    }
  }, [selectedTab]);

  const getReadyToPrintCharts = useCallback(async () => {
    if (clerkId) {
      try {
        const response = await ChartsService.adminGetClerkChartsByStatus(
          clerkId,
          ChartStatusEnum.Completed,
          false,
          true
        );
        setReadyToPrintCharts(response);
      } catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkId]);

  const getOtherChartTab = useCallback(
    async (query: string, status: string) => {
      if (debouncedQuery) {
        setLoading(true);
      }
      if (clerkId) {
        try {
          setLoading(true);
          status = status.replace(/\s/g, "") // Remove the whitespace in the middle of "In Progress"
          const newCharts = await ChartsService.adminGetClerkChartsByStatus(
            clerkId as string,
            status as any,
            false,
            false,
            query === '' || !query ? undefined : query
          );
          const electionCounties = newCharts.map((chart) => {
            return chart.county;
          });
          const uniqueCounties = electionCounties.filter(function (
            elem,
            pos,
            arr
          ) {
            return arr.indexOf(elem) === pos;
          });
          setCounties(uniqueCounties);

          // for each of the unique counties, look for election dates
          let uniqueCountiesWithElectionDates: string[] = [];
          uniqueCounties.forEach((county) => {
            let electionDates: string[] = [];
            newCharts.forEach((chart) => {
              if (chart.county === county) {
                electionDates.push(chart.electionDate);
              }
            });
            const uniqueElectionDates = electionDates.filter(function (
              elem,
              pos,
              arr
            ) {
              return arr.indexOf(elem) === pos;
            });
            uniqueElectionDates.forEach((electionDate) => {
              uniqueCountiesWithElectionDates.push(`${county} - ${electionDate}`);
            });
          });
          const chartsGroupedByCounty = uniqueCountiesWithElectionDates.map(
            (countyWithDate) => {
              const parts = countyWithDate.split(" - ");
              const county = parts[0];
              const electionDate = parts[1];
              return newCharts
                .filter(
                  (chart: any) =>
                    chart.county === county && chart.electionDate === electionDate
                )
                .map((c: any) => {
                  return { ...c, deleteChart, regenerateChart };
                });
            }
          );
          setOtherChartTab(chartsGroupedByCounty);
        } catch (e) {
        } finally {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedQuery, otherChartStatus, otherChartTab]
  );

  useEffect(() => {
    getOtherChartTab(otherChartQuery, otherChartStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, otherChartStatus]);

  const getPrintedCharts = useCallback(async () => {
    if (clerkId) {
      try {
        const response = await ChartsService.adminGetClerkChartsByStatus(
          clerkId,
          ChartStatusEnum.Completed,
          true,
          false
        );
        setPrintedCharts(response);
      } catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkId]);

  useEffect(() => {
    if (clerkId && selectedTab === 0) {
      getReadyToPrintCharts();
    }
    if (clerkId && selectedTab === 2) {
      getPrintedCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkId, selectedTab]);

  useEffect(() => {
    if (clerkId && otherChartQuery !== "") {
      getOtherChartTab(otherChartQuery, otherChartStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkId, otherChartQuery, otherChartStatus]);

  const tabs: FilterTab[] = [
    {
      name: "Ready To Print Charts",
      count: readyToPrintCharts ? readyToPrintCharts.length : undefined,
    },
    {
      name: "Other Charts",
      count: otherChartTab ? getOtherChartCount(otherChartTab) : undefined,
    },
    {
      name: "Printed Charts",
      count: printedCharts ? printedCharts.length : undefined,
    },
    {
      name: "", // This is to make the last tab not against the right side of the screen
    },
  ];

  const selectedColor = "border-red-400";

  const getClerk = useCallback(async (clerkId: string) => {
    try {
      const clerkAccount = await AccountService.getClerk(clerkId);
      setClerk(clerkAccount);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (clerkId) {
      getClerk(clerkId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkId]);

  const updateStatus = (newStatus: ChartStatus): any => {
    setOtherChartStatus(newStatus);
    setSearchParams({ status: newStatus });
    getOtherChartTab(otherChartQuery, newStatus);
  };

  const downloadAll = async (chartIds: string[], type: "charts" | "decks") => {
    await toast.promise(ChartsService.downloadAll(chartIds, type === "decks"), {
      loading: "Loading...",
      error: ({ error }) => error ?? "Files download failed!",
      success: ({ data, filename }) => {
        const href = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        return "Successful download!";
      },
    });
  };

  function getOtherChartCount(array: any[][]): number {
    let totalCount = 0;

    for (const outerArray of array) {
        for (let _ of outerArray) {
            totalCount++;
        }
    }

    return totalCount;
}

  const downloadChart = async (chartId: string, type: "download" | "print") => {
    await toast.promise(ChartsService.getS3Link(chartId, type), {
      loading: "Loading...",
      success: (downloadLink) => {
        const a = document.createElement("a");
        a.href = downloadLink;
        document.body.appendChild(a);
        a.click();
        a.parentNode!.removeChild(a);
        return "Successful download!";
      },
      error: "Download failed!",
    });
  };

  const getDate = (date: string): string => {
    const d = new Date(date);
    return `${d.toLocaleString("en-US", {
      month: "long",
    })} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  };

  const printAll = async (chartIds: string[]) => {
    await toast.promise(
      ChartsService.printPrintRequestedCharts(chartIds, false),
      {
        loading: "Loading...",
        error: ({ error }) => error ?? "Files download failed!",
        success: ({ data, filename }) => {
          const href = URL.createObjectURL(data);
          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          return "Successful download!";
        },
      }
    );
  };

  const downloadReadyForPrintCharts = async (chartIds: string[]) => {
    await toast.promise(
      ChartsService.downloadPrintRequestedCharts(chartIds, false),
      {
        loading: "Loading...",
        error: "Files download failed!",
        success: ({ data, filename }) => {
          const href = URL.createObjectURL(data);
          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          return "Successful download!";
        },
      }
    );
  };

  const getBaseUrl = (chartType: ChartTypeEnum) => {
    if (chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "/elections/general";
    } else if (chartType === ChartTypeEnum.NON_PARTISAN) {
      return "/elections/non-partisan";
    } else if (chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "/elections/partisan";
    } else if (chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "/elections/presidential";
    }
  };

  const duplicateChart = (chartId: string, chartType: ChartTypeEnum) => {
    navigate(`${getBaseUrl(chartType)}/duplicate/${chartId}`);
  };

  return {
    clerk,
    tabs,
    selectedTab,
    setSelectedTab,
    selectedColor,
    readyToPrintCharts,
    otherChartTab,
    printedCharts,
    counties,
    downloadAll,
    downloadChart,
    getDate,
    otherChartStatus,
    updateStatus,
    debouncedQuery,
    otherChartQuery,
    setOtherChartQuery,
    printAll,
    downloadReadyForPrintCharts,
    duplicateChart,
    markChartsAsPrinted,
  };
}
